import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import SearchableSelect from 'components/SearchableSelect'
import Avatar from 'components/Avatar'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { hideEmail } from 'utils/user'
import { visibleAccessRows } from 'features/accessControl/visibleUsers'
import { formatDate } from 'utils/date'
import { ROLE_OPTIONS, roleLabel } from 'utils/roleOptions'
import { LAST_ACTIVE_HELP, lastActiveText, lastActiveTitle, lastActiveSortValue } from 'features/accessControl/lastActive'
import { ink, line, radius, surface, font, transition, bp as bpToken } from 'features/dashboard/tokens'
import { RAG } from 'features/dashboard/charts/scoreColour'
import RolesExplainer from 'features/organisation/RolesExplainer'
import SuperAdminBadge from 'features/organisation/SuperAdminBadge'
import { ROLE_LABELS, SUPER_ADMIN_LABEL } from 'utils/roles'

const PAGE = 15
const DIRECT_BG = '#e6fbfd'
const DIRECT_FG = '#0f766e'
const ROLE_COL_MIN_WIDTH = '180px'

const destName = (row) => (row.sourceLabel || 'source').replace(/^via\s+/i, '')
const fullName = (row) => `${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim()
const liftedOrganisation = (row) => (row.isSuperAdmin && row.role === 'owner' && !row.roleMixed ? row.superAdminOrganisation || null : null)
const ACCESS_FILTER_OPTIONS = [
  { value: 'direct', label: 'Direct' },
  { value: 'estate', label: 'Via an estate' },
  { value: 'portfolio', label: 'Via a portfolio' },
  { value: 'building', label: 'Via a building' },
]
const SORTERS = {
  name: (r) => fullName(r).toLowerCase(),
  role: (r) => roleLabel(r.role).toLowerCase(),
  access: (r) => `${r.source === 'direct' ? '0' : '1'}${(r.sourceLabel || '').toLowerCase()}`,
  lastActive: (r) => lastActiveSortValue(r.user?.lastActivityAt),
}
const addedLine = (row) => {
  if (!row.addedAt) return null
  const who = row.addedBy ? [row.addedBy.firstName, row.addedBy.lastName].filter(Boolean).join(' ').trim() : ''
  return who ? `Added ${formatDate(row.addedAt)} by ${who}` : `Added ${formatDate(row.addedAt)}`
}

const AccessCell = ({ row, onJumpToSource }) => {
  if (row.source === 'direct') {
    return (
      <AccessLine>
        <DirectBadge>Direct</DirectBadge>
        {row.appliesToCount > 1 && <MutedText>· all {row.appliesToCount} buildings</MutedText>}
      </AccessLine>
    )
  }
  if (row.source === 'building' && row.appliesToCount > 1) {
    return (
      <AccessLine>
        <InheritPill>
          <SubdirectoryArrowRightIcon style={{ fontSize: 14, color: ink.mute }} />
          {row.appliesToCount} buildings
        </InheritPill>
      </AccessLine>
    )
  }
  const canOpen = !!row.sourceId && !!onJumpToSource
  return (
    <AccessLine>
      {canOpen ? (
        <InheritLink type='button' onClick={() => onJumpToSource(row)} title={`Open ${destName(row)}`}>
          <SubdirectoryArrowRightIcon style={{ fontSize: 14 }} />
          {row.sourceLabel}
        </InheritLink>
      ) : (
        <InheritPill>
          <SubdirectoryArrowRightIcon style={{ fontSize: 14, color: ink.mute }} />
          {row.sourceLabel}
        </InheritPill>
      )}
      {row.appliesToCount > 1 && <MutedText>· {row.appliesToCount} buildings</MutedText>}
    </AccessLine>
  )
}

const BuildingsPanel = ({ row, canManage, onEditBuildingRole, onRemoveBuilding, onOpenBuilding }) => {
  const name = `${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim() || 'this person'
  return (
    <PanelWrap>
      <PanelHeading>Access by building</PanelHeading>
      <PanelList>
        {row.buildings.map((b) => (
          <PanelRow key={b.rowId}>
            <PanelBpName>
              <ApartmentOutlinedIcon style={{ fontSize: 18, color: ink.base, flexShrink: 0 }} />
              <BuildingLinkBtn type='button' onClick={() => onOpenBuilding(b)} title={`Open ${b.bpName}`}>{b.bpName}</BuildingLinkBtn>
              {b.source !== 'building' && b.sourceLabel && <PanelSourceLabel>{b.sourceLabel}</PanelSourceLabel>}
            </PanelBpName>
            <PanelControls>
              <PanelRoleField>
                {canManage ? (
                  <SearchableSelect ariaLabel={`Role on ${b.bpName}`} value={b.role} options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))} onChange={(v) => onEditBuildingRole(b, v)} />
                ) : (
                  <RoleChip>{roleLabel(b.role)}</RoleChip>
                )}
              </PanelRoleField>
              {canManage && (
                <DangerBtn type='button' onClick={() => { if (window.confirm(`Remove ${name} from ${b.bpName}?`)) onRemoveBuilding(b) }}><PersonRemoveOutlinedIcon style={{ fontSize: 15 }} /> Remove from this building</DangerBtn>
              )}
            </PanelControls>
          </PanelRow>
        ))}
      </PanelList>
    </PanelWrap>
  )
}

const PeopleTable = ({ rows = [], currentUserId = null, canManage, isOwner = false, supportsPerFile, selectedIds = new Set(), onToggleRow = () => {}, onToggleAll = () => {}, onChangeRole, onRevoke, onEditFiles, onJumpToSource, onEditBuildingRole, onRemoveBuilding, onOpenBuilding = () => {} }) => {
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState(null)
  const [accessFilter, setAccessFilter] = useState(null)
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' })
  const [visible, setVisible] = useState(PAGE)
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [liftNoteIds, setLiftNoteIds] = useState(new Set())

  const changeRole = (r, value) => {
    if (r.isSuperAdmin && r.superAdminOrganisation && value !== 'owner') setLiftNoteIds((prev) => new Set(prev).add(r.id))
    onChangeRole(r, value)
  }

  const filtered = useMemo(() => {
    const clean = visibleAccessRows(rows, currentUserId)
    const q = search.trim().toLowerCase()
    const matched = clean.filter((r) => {
      if (roleFilter && r.role !== roleFilter && !(r.roleMixed && (r.buildings || []).some((b) => b.role === roleFilter))) return false
      if (accessFilter && (r.source || 'direct') !== accessFilter) return false
      if (!q) return true
      return `${fullName(r)} ${r.user?.email || ''} ${roleLabel(r.role)} ${r.sourceLabel || ''}`.toLowerCase().includes(q)
    })
    const pick = SORTERS[sort.key] || SORTERS.name
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...matched].sort((a, b) => {
      const av = pick(a)
      const bv = pick(b)
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return fullName(a).localeCompare(fullName(b))
    })
  }, [rows, currentUserId, search, roleFilter, accessFilter, sort])

  const sortBy = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'lastActive' ? 'desc' : 'asc' }))
    setVisible(PAGE)
  }
  const sortMark = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')
  const columnCount = isOwner ? 7 : 6

  const shown = filtered.slice(0, visible)
  const firstEditableId = shown.find((r) => !r.roleMixed && r.editableHere && canManage)?.id
  const visibleSelectableIds = useMemo(() => shown.filter((r) => r.editableHere && canManage).map((r) => r.id), [shown, canManage])
  const allVisibleSelected = visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selectedIds.has(id))

  const toggleExpand = (id) => setExpandedIds((prev) => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  return (
    <Wrap>
      <Filters>
        <SearchInput placeholder='Search people…' value={search} onChange={(e) => { setSearch(e.target.value); setVisible(PAGE) }} />
        <FilterField>
          <SearchableSelect ariaLabel='Filter by role' placeholder='Any role' clearable clearLabel='Any role' value={roleFilter} options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))} onChange={(v) => { setRoleFilter(v || null); setVisible(PAGE) }} />
        </FilterField>
        <FilterField>
          <SearchableSelect ariaLabel='Filter by how access was given' placeholder='Any access' clearable clearLabel='Any access' searchable={false} value={accessFilter} options={ACCESS_FILTER_OPTIONS} onChange={(v) => { setAccessFilter(v || null); setVisible(PAGE) }} />
        </FilterField>
      </Filters>
      <TableScroll>
        <Table $cols={columnCount}>
          <thead>
            <tr>
              <Th $chevron />
              <Th $checkbox><Checkbox type='checkbox' aria-label='Select all' checked={allVisibleSelected} disabled={visibleSelectableIds.length === 0} onChange={() => onToggleAll(visibleSelectableIds)} /></Th>
              <SortTh $active={sort.key === 'name'} onClick={() => sortBy('name')} aria-sort={sort.key === 'name' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>Person{sortMark('name')}</SortTh>
              <SortTh $active={sort.key === 'role'} onClick={() => sortBy('role')} aria-sort={sort.key === 'role' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <RoleHeaderInner>Role{sortMark('role')}<span onClick={e => e.stopPropagation()}><RolesExplainer /></span></RoleHeaderInner>
              </SortTh>
              <SortTh $active={sort.key === 'access'} onClick={() => sortBy('access')} aria-sort={sort.key === 'access' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>Access{sortMark('access')}</SortTh>
              {isOwner && <SortTh $active={sort.key === 'lastActive'} onClick={() => sortBy('lastActive')} aria-sort={sort.key === 'lastActive' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} title={LAST_ACTIVE_HELP}>Last active{sortMark('lastActive')}</SortTh>}
              <Th />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const hasBuildings = r.buildings && r.buildings.length > 0
              const isExpanded = expandedIds.has(r.id)
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    <Td $chevron>
                      {hasBuildings && (
                        <ExpandBtn type='button' aria-label={isExpanded ? 'Collapse access by building' : 'Expand access by building'} aria-expanded={isExpanded} $open={isExpanded} onClick={() => toggleExpand(r.id)}>
                          <ChevronRightIcon style={{ fontSize: 20 }} />
                        </ExpandBtn>
                      )}
                    </Td>
                    <Td $checkbox>
                      {r.editableHere && canManage && (
                        <Checkbox type='checkbox' aria-label={`Select ${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim() || 'Select person'} checked={selectedIds.has(r.id)} onChange={() => onToggleRow(r.id)} />
                      )}
                    </Td>
                    <Td>
                      <Person>
                        <Avatar firstName={r.user?.firstName} lastName={r.user?.lastName} email={r.user?.email} size={36} />
                        <div>
                          <Name>{r.user?.firstName} {r.user?.lastName}</Name>
                          <Em>{hideEmail(r.user?.email)}</Em>
                          {addedLine(r) && <AddedMeta title={addedLine(r)}>{addedLine(r)}</AddedMeta>}
                        </div>
                      </Person>
                    </Td>
                    <Td $minWidth={ROLE_COL_MIN_WIDTH}>
                      <RoleField data-tour-id={r.id === firstEditableId ? 'accessControl.first-role' : undefined}>
                        <RoleFieldRow>
                          {r.roleMixed ? (
                            <RoleChip title='Role varies across buildings. Expand this row to see each one.'>Mixed</RoleChip>
                          ) : r.editableHere && canManage ? (
                            <SearchableSelect ariaLabel='Role' value={r.role} options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))} onChange={(v) => changeRole(r, v)} />
                          ) : (<RoleChip>{roleLabel(r.role)}</RoleChip>)}
                          {r.isSuperAdmin && <SuperAdminBadge />}
                        </RoleFieldRow>
                        {liftedOrganisation(r) && <RoleSource>{`${ROLE_LABELS.owner}, as ${SUPER_ADMIN_LABEL} of ${liftedOrganisation(r)}`}</RoleSource>}
                        {liftNoteIds.has(r.id) && r.superAdminOrganisation && (
                          <LiftNote role='status'>{`They stay an ${ROLE_LABELS.owner} on this building while they are a ${SUPER_ADMIN_LABEL} of ${r.superAdminOrganisation}.`}</LiftNote>
                        )}
                      </RoleField>
                    </Td>
                    <Td><AccessCell row={r} onJumpToSource={onJumpToSource} /></Td>
                    {isOwner && (
                      <Td>
                        <LastActive $never={!r.user?.lastActivityAt} title={lastActiveTitle(r.user?.lastActivityAt)}>{lastActiveText(r.user?.lastActivityAt)}</LastActive>
                      </Td>
                    )}
                    <Td $right>
                      {r.editableHere && canManage ? (
                        <RowActions>
                          {supportsPerFile && <LinkBtn onClick={() => onEditFiles(r)}><FolderOutlinedIcon style={{ fontSize: 15 }} /> Files</LinkBtn>}
                          <DangerBtn data-tour-id={r.id === firstEditableId ? 'accessControl.first-remove' : undefined} onClick={() => onRevoke(r)}><PersonRemoveOutlinedIcon style={{ fontSize: 15 }} /> Remove</DangerBtn>
                        </RowActions>
                      ) : r.source === 'building' && r.appliesToCount > 1 ? null : (
                        <LinkBtn onClick={() => onJumpToSource(r)} title='Manage this person where their access was granted'>Open {destName(r)} <ArrowForwardRoundedIcon style={{ fontSize: 14 }} /></LinkBtn>
                      )}
                    </Td>
                  </tr>
                  {isExpanded && hasBuildings && (
                    <tr>
                      <ExpandedTd colSpan={columnCount}>
                        <BuildingsPanel row={r} canManage={canManage} onEditBuildingRole={onEditBuildingRole} onRemoveBuilding={onRemoveBuilding} onOpenBuilding={onOpenBuilding} />
                      </ExpandedTd>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </Table>
      </TableScroll>
      <Info>Showing {shown.length} of {filtered.length}{filtered.length !== rows.length ? ` (${rows.length} in total)` : ''}</Info>
      <InfiniteScrollSentinel hasMore={visible < filtered.length} loading={false} onLoadMore={() => setVisible((v) => v + PAGE)} label='Loading more…' />
    </Wrap>
  )
}

export default PeopleTable

const Wrap = styled.div``
const Filters = styled.div`display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:12px;`
const SearchInput = styled.input`flex:1 1 220px;max-width:320px;border:1px solid ${line.strong};border-radius:${radius.sm};padding:9px 12px;font-size:${font.body};color:${ink.base};transition:${transition};&::placeholder{color:${ink.mute};}&:focus{outline:none;border-color:${({ theme }) => theme.colors.secondary};}@media (max-width:${bpToken.mobile}){max-width:none;flex-basis:100%;}`
const FilterField = styled.div`flex:0 1 200px;min-width:160px;@media (max-width:${bpToken.mobile}){flex:1 1 calc(50% - 5px);min-width:0;}`
const TableScroll = styled.div`overflow-x:auto;@media (max-width:${bpToken.mobile}){overflow-x:visible;}`
const Table = styled.table`
  width:100%;min-width:${({ $cols }) => ($cols > 6 ? '760px' : '620px')};border-collapse:collapse;tbody tr:hover td{background:${surface.wash};}
  @media (max-width:${bpToken.mobile}){
    min-width:0;
    thead{display:none;}
    tbody tr{display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;padding:12px 2px;border-bottom:1px solid ${line.base};}
    tbody tr:hover td{background:transparent;}
    td{border-bottom:none !important;padding:0 !important;min-width:0 !important;text-align:left !important;}
    td:nth-child(3){flex:1 1 auto;min-width:0;}
    td:nth-child(4){flex:1 1 100%;}
    td:nth-child(5){flex:1 1 auto;display:flex;align-items:center;}
    td:nth-child(6):not(:last-child){flex:1 1 100%;}
    td:last-child{flex:0 0 auto;margin-left:auto;}
  }
`
const Th = styled.th`text-align:left;font-size:10.5px;text-transform:uppercase;letter-spacing:0.05em;color:${ink.mute};font-weight:800;padding:10px 14px;border-bottom:1px solid ${line.base};white-space:nowrap;${({ $checkbox }) => ($checkbox ? 'width:36px;padding-right:0;' : '')}${({ $chevron }) => ($chevron ? 'width:36px;padding-right:0;' : '')}`
const SortTh = styled(Th)`cursor:pointer;user-select:none;color:${({ $active }) => ($active ? ink.base : ink.mute)};transition:${transition};&:hover{color:${ink.base};}`
const Td = styled.td`padding:13px 14px;border-bottom:1px solid ${line.base};vertical-align:middle;text-align:${({ $right }) => ($right ? 'right' : 'left')};${({ $minWidth }) => ($minWidth ? `min-width:${$minWidth};` : '')}${({ $checkbox }) => ($checkbox ? 'width:36px;padding-right:0;' : '')}${({ $chevron }) => ($chevron ? 'width:36px;padding-right:0;' : '')}`
const ExpandedTd = styled.td`padding:0;border-bottom:1px solid ${line.base};`
const Checkbox = styled.input`width:17px;height:17px;accent-color:#33becc;cursor:pointer;margin:0;&:disabled{cursor:not-allowed;opacity:0.35;}`
const ExpandBtn = styled.button`display:inline-flex;align-items:center;justify-content:center;min-width:36px;min-height:36px;padding:0;border:none;background:transparent;color:${ink.mute};border-radius:${radius.xs};cursor:pointer;transition:${transition};svg{transition:transform 160ms ease;transform:rotate(${({ $open }) => ($open ? '90deg' : '0deg')});}&:hover{color:${ink.base};background:${surface.wash};}`
const Person = styled.div`display:flex;align-items:center;gap:12px;`
const Name = styled.div`font-size:${font.body};color:${ink.base};font-weight:600;`
const Em = styled.div`font-size:${font.small};color:${ink.mute};margin-top:1px;`
const AddedMeta = styled.div`font-size:${font.caption};color:${ink.mute};opacity:0.85;margin-top:2px;`
const RoleField = styled.div`width:100%;min-width:${ROLE_COL_MIN_WIDTH};@media (max-width:${bpToken.mobile}){min-width:0;}`
const RoleFieldRow = styled.div`display:flex;align-items:center;gap:8px;flex-wrap:wrap;`
const RoleSource = styled.div`font-size:${font.small};color:${ink.soft};margin-top:6px;`
const LiftNote = styled.div`font-size:${font.small};color:${ink.base};background:${surface.wash};border-radius:${radius.sm};padding:6px 10px;margin-top:6px;`
const RoleHeaderInner = styled.span`display:inline-flex;align-items:center;gap:2px;`
const RoleChip = styled.span`display:flex;align-items:center;width:100%;min-height:38px;box-sizing:border-box;border:1px solid ${line.strong};border-radius:${radius.sm};padding:8px 12px;font-size:${font.body};font-weight:600;color:${ink.soft};background:${surface.wash};cursor:default;`
const AccessLine = styled.div`display:flex;align-items:center;gap:6px;flex-wrap:wrap;`
const DirectBadge = styled.span`display:inline-flex;align-items:center;background:${DIRECT_BG};color:${DIRECT_FG};border-radius:${radius.sm};padding:3px 9px;font-weight:700;font-size:${font.small};white-space:nowrap;`
const InheritPill = styled.span`display:inline-flex;align-items:center;gap:5px;background:${surface.wash};color:${ink.soft};border-radius:${radius.sm};padding:3px 9px;font-weight:600;font-size:${font.small};white-space:nowrap;`
const InheritLink = styled.button`display:inline-flex;align-items:center;gap:5px;background:${surface.wash};color:${({ theme }) => theme.colors.secondary};border:1px solid transparent;border-radius:${radius.sm};padding:3px 9px;font-weight:700;font-size:${font.small};white-space:nowrap;cursor:pointer;transition:${transition};&:hover{border-color:${({ theme }) => theme.colors.secondary};background:#f0fbfc;text-decoration:underline;}`
const LastActive = styled.span`font-size:${font.small};font-weight:600;white-space:nowrap;color:${({ $never }) => ($never ? ink.mute : ink.soft)};cursor:help;`
const MutedText = styled.span`color:${ink.mute};font-size:${font.small};white-space:nowrap;`
const RowActions = styled.div`display:flex;gap:8px;justify-content:flex-end;@media (max-width:${bpToken.mobile}){justify-content:flex-start;}`
const LinkBtn = styled.button`display:inline-flex;align-items:center;gap:5px;padding:6px 12px;border:1px solid ${line.strong};border-radius:${radius.sm};background:${surface.card};color:${({ theme }) => theme.colors.secondary};font-size:12.5px;font-weight:700;cursor:pointer;transition:${transition};&:hover{border-color:${({ theme }) => theme.colors.secondary};background:#f0fbfc;}`
const DangerBtn = styled.button`display:inline-flex;align-items:center;gap:5px;padding:6px 12px;border:1px solid ${line.strong};border-radius:${radius.sm};background:${surface.card};color:${RAG.critical.fg};font-size:12.5px;font-weight:700;cursor:pointer;transition:${transition};&:hover{border-color:${RAG.critical.fg};background:${RAG.critical.bg};}`
const Info = styled.div`font-size:14px;color:#475569;margin:8px 2px;`
const PanelWrap = styled.div`background:${surface.wash};border-left:3px solid ${({ theme }) => theme.colors.secondary};padding:14px 18px 14px 22px;`
const PanelHeading = styled.div`font-size:${font.caption};font-weight:800;letter-spacing:0.05em;text-transform:uppercase;color:${ink.mute};margin-bottom:10px;`
const PanelList = styled.div`display:flex;flex-direction:column;`
const PanelRow = styled.div`display:flex;align-items:center;justify-content:space-between;gap:16px;padding:10px 0;border-bottom:1px solid ${line.base};&:last-child{border-bottom:none;}@media (max-width: 768px){flex-direction:column;align-items:flex-start;gap:10px;}`
const PanelBpName = styled.div`display:flex;align-items:center;gap:8px;font-size:${font.body};font-weight:600;color:${ink.base};flex:1 1 auto;min-width:0;`
const BuildingLinkBtn = styled.button`background:none;border:none;padding:0;margin:0;font-size:${font.body};font-weight:600;color:${({ theme }) => theme.colors.secondary};cursor:pointer;text-align:left;transition:${transition};&:hover{text-decoration:underline;}`
const PanelSourceLabel = styled.span`font-size:${font.small};font-weight:500;color:${ink.mute};white-space:nowrap;`
const PanelControls = styled.div`display:flex;align-items:center;gap:10px;flex:0 0 auto;@media (max-width: 768px){width:100%;}`
const PanelRoleField = styled.div`width:200px;flex:0 0 200px;@media (max-width: 768px){flex:1 1 auto;width:auto;}`
